/**
 * Typed wrapper around the QR Studio API (/api/v1/*).
 * Contract as agreed with @qrstudio/api (see docs/02-contracts.md).
 */

export type Bank = {
  bin: string;
  code: string;
  shortName: string;
  name: string;
  logo: string;
  swift?: string;
};

export type BanksResponse = {
  banks: Bank[];
};

export type ValidationError = {
  field: string;
  code: string;
  message: string;
};

export type ValidateResponse = {
  valid: boolean;
  errors: ValidationError[];
};

export type VietQRData = {
  bankBin: string;
  accountNumber: string;
  amount?: number;
  purpose?: string;
};

export type PreviewRequest = {
  data: VietQRData;
  modulePx?: number;
  format?: "svg" | "png";
  /** Màu module tối (mặc định #000000 phía server) — dùng ở chế độ Nâng cao để khớp `QRSlot.style.foreground`. */
  foreground?: string;
  /** Màu nền (mặc định #FFFFFF phía server) — khớp `QRSlot.style.background`. */
  background?: string;
};

export type PreviewResponse = {
  payload: string;
  svg: string;
  size: number;
  version: number;
  ecc: string;
};

export type TemplateSummary = {
  templateId: string;
  version: number;
  name: string;
  description?: string;
  category: string;
  aspectRatio: string;
  bankingSafe: boolean;
};

export type TemplatesResponse = {
  templates: TemplateSummary[];
  nextCursor: string | null;
};

export type RenderWarning = {
  code: string;
  message: string;
};

export type RenderRequest = {
  templateId: string;
  templateVersion?: number;
  qrData: VietQRData;
  variables?: Record<string, string | number>;
  format?: "svg";
};

/**
 * Nhãn scanability thật từ server (`@qrstudio/scanability`, docs/30-scanability.md) — nguồn
 * sự thật DUY NHẤT cho trạng thái quét/banking-safe (I5, docs/01-architecture.md §1). Trước
 * khi có field này, `POST /qr/render` chỉ trả `warnings[]` (cảnh báo renderer, KHÔNG phải
 * kết quả scanability thật) và `apps/web/src/editor/scanability.ts` phải tự đoán bằng
 * heuristic client — lỗi kiến trúc đã sửa ở nhiệm vụ này.
 */
export type ScanabilityLabel = "BANKING_SAFE" | "GOOD" | "RISKY" | "UNSAFE";

export type ScanabilityCheck = {
  id: string;
  status: string;
  severity: string;
  message: string;
  repairable: boolean;
};

export type ScanabilitySlotReport = {
  slotId: string;
  score: number;
  label: ScanabilityLabel;
  bankingSafe: boolean;
  checks: ScanabilityCheck[];
  summary: string;
};

/** Kết quả scanability cấp template — `bankingSafe`/`score`/`label` là WORST-CASE của mọi qrSlot (`slots[]`). */
export type RenderScanabilityReport = {
  score: number;
  label: ScanabilityLabel;
  bankingSafe: boolean;
  checks: ScanabilityCheck[];
  summary: string;
  slots: ScanabilitySlotReport[];
};

export type RenderResponse = {
  payload: string;
  svg: string;
  template: { templateId: string; version: number; name: string };
  warnings: RenderWarning[];
  /**
   * `null` CHỈ khi server không kiểm tra được (xem `warnings` lúc đó sẽ có cảnh báo
   * `SCANABILITY_UNAVAILABLE` giải thích) — KHÔNG BAO GIỜ coi `null` là "đã đạt". UI phải
   * hiện trạng thái trung lập ("Chưa kiểm tra được"), không phải badge xanh.
   */
  scanability: RenderScanabilityReport | null;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const BASE = "/api/v1";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError("network");
  }

  if (!res.ok) {
    throw new ApiError(`http_${res.status}`, res.status);
  }

  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError("bad_json");
  }
}

export function getBanks(): Promise<BanksResponse> {
  return request<BanksResponse>("/banks", { method: "GET" });
}

/**
 * POST /api/v1/qr/validate
 * Body shape: a BARE VietQRData object, NOT wrapped — e.g. { bankBin, accountNumber, amount?, purpose? }.
 * Do not wrap it in { data: ... } — that shape is only for /qr/preview.
 */
export function validateQr(data: VietQRData): Promise<ValidateResponse> {
  return request<ValidateResponse>("/qr/validate", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/**
 * POST /api/v1/qr/preview
 * Body shape: WRAPPED — { data: { bankBin, accountNumber, amount?, purpose? }, format?, modulePx? }.
 * Plain (template-less) QR preview. No templateId/qrData fields here — those belong to /qr/render.
 */
export function previewQr(body: PreviewRequest): Promise<PreviewResponse> {
  return request<PreviewResponse>("/qr/preview", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * GET /api/v1/templates?category=&q=&limit=20&cursor=
 * Query params only, all optional except limit (defaulted client-side).
 * Response: { templates: TemplateSummary[], nextCursor: string | null }.
 */
export function getTemplates(params: {
  category?: string;
  q?: string;
  limit?: number;
  cursor?: string | null;
} = {}): Promise<TemplatesResponse> {
  const usp = new URLSearchParams();
  if (params.category) usp.set("category", params.category);
  if (params.q) usp.set("q", params.q);
  usp.set("limit", String(params.limit ?? 20));
  if (params.cursor) usp.set("cursor", params.cursor);
  return request<TemplatesResponse>(`/templates?${usp.toString()}`, { method: "GET" });
}

/**
 * POST /api/v1/qr/render
 * Body shape: { templateId, templateVersion?, qrData: { bankBin, accountNumber, amount?, purpose? }, variables?, format? }.
 * IMPORTANT: the bank/account field here is named `qrData`, NOT `data` (that name is only for /qr/preview).
 * Response: { payload, svg, template: { templateId, version, name }, warnings: [{code, message}] }.
 */
export function renderQr(body: RenderRequest): Promise<RenderResponse> {
  return request<RenderResponse>("/qr/render", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export type ScanabilityRequest = {
  qrData: VietQRData;
  /** `QRSlot[]` thật của `@qrstudio/template-engine` — kiểu `unknown` ở đây để api.ts không phải import type đó (tránh vòng phụ thuộc không cần thiết); caller (editor) tự đảm bảo đúng shape `QRSlot`. */
  qrSlots: unknown[];
};

export type ScanabilityRenderResponse = {
  scanability: RenderScanabilityReport | null;
  warnings: RenderWarning[];
};

/**
 * POST /api/v1/qr/scanability — chạy `@qrstudio/scanability` THẬT trên `qrSlots` CHƯA LƯU
 * (chế độ Nâng cao đang chỉnh dở, chưa có `templateId` để gọi `/qr/render`). Dùng làm nguồn
 * sự thật cho `apps/web/src/editor/useServerScanability.ts` — heuristic client trong
 * `editor/scanability.ts` chỉ còn là phản hồi tức thì trong lúc kéo thả, kết quả gọi hàm này
 * LUÔN ghi đè khi có.
 */
export function getQrScanability(body: ScanabilityRequest): Promise<ScanabilityRenderResponse> {
  return request<ScanabilityRenderResponse>("/qr/scanability", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * "Đưa QR vào ảnh của bạn" — luồng upload/detect/place.
 * Hợp đồng theo agent @qrstudio/api đang triển khai song song (chưa xác nhận triển khai xong
 * bằng curl thật lúc code — xem báo cáo bàn giao). Toạ độ `Quad` là TL,TR,BR,BL, đơn vị pixel
 * trên ẢNH GỐC (khớp `width`/`height` trả về từ POST /uploads), KHÔNG chuẩn hoá 0..1.
 */
export type Pt = [number, number];
export type Quad = [Pt, Pt, Pt, Pt];

export type UploadResponse = {
  fileId: string;
  width: number;
  height: number;
  mimeType: string;
};

export type PlacementCandidate = {
  id: string;
  quad: Quad;
  surfaceKind: string;
  score: number;
  areaRatio: number;
  flatness: number;
  meanLuma: number;
  contrastEstimate: number;
  textureBusyness: number;
  reasons: string[];
};

export type DetectResponse = {
  candidates: PlacementCandidate[];
};

export type PlaceRequestBody = {
  quad: Quad;
  qrData: VietQRData;
  slotType?: string;
  style?: Record<string, unknown>;
  watermark?: boolean;
};

export type ScanabilityResult = {
  score: number;
  label: string;
  bankingSafe: boolean;
  warnings: string[];
};

export type PlaceResponse = {
  payload: string;
  qrSvg: string;
  quad: Quad;
  scanability: ScanabilityResult;
};

/**
 * POST /api/v1/uploads — multipart/form-data, field "file".
 * Dùng XMLHttpRequest (thay vì fetch) để có tiến độ tải lên (`onProgress`, 0..100).
 */
export function uploadImage(
  blob: Blob,
  filename: string,
  onProgress?: (percent: number) => void,
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append("file", blob, filename);

    xhr.open("POST", `${BASE}/uploads`);
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadResponse);
        } catch {
          reject(new ApiError("bad_json"));
        }
      } else {
        reject(new ApiError(`http_${xhr.status}`, xhr.status));
      }
    };
    xhr.onerror = () => reject(new ApiError("network"));
    xhr.send(form);
  });
}

/** POST /api/v1/uploads/:fileId/detect — không cần body. */
export function detectPlacement(fileId: string): Promise<DetectResponse> {
  return request<DetectResponse>(`/uploads/${fileId}/detect`, { method: "POST" });
}

export type TemplateDetailResponse = {
  templateId: string;
  version: number;
  name: string;
  description: string;
  category: string;
  type: string;
  schemaVersion: string;
  /** JSON đầy đủ khớp `Template` (`@qrstudio/template-engine`) — chỉ dùng ở chế độ Nâng cao. */
  config: unknown;
  previewR2Key: string | null;
  isPublic: boolean;
  isAiGenerated: boolean;
  parentTemplateId: string | null;
  scanabilityScore: number | null;
  bankingSafe: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * GET /api/v1/templates/:id (bản PUBLIC mới nhất) hoặc GET /api/v1/templates/:id/versions/:version
 * (đúng phiên bản). Dùng riêng cho chế độ Nâng cao — chế độ Đơn giản không cần `config` đầy đủ.
 */
export function getTemplateDetail(templateId: string, version?: number): Promise<TemplateDetailResponse> {
  const path = version ? `/templates/${templateId}/versions/${version}` : `/templates/${templateId}`;
  return request<TemplateDetailResponse>(path, { method: "GET" });
}

/** POST /api/v1/uploads/:fileId/place */
export function placeQr(fileId: string, body: PlaceRequestBody): Promise<PlaceResponse> {
  return request<PlaceResponse>(`/uploads/${fileId}/place`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * "Trộn ảnh vào mã QR" (QR halftone) — luồng thứ hai bên cạnh "Đặt mã QR lên ảnh" ở trên,
 * dùng chung bước upload. `blendStrength` là số nội bộ server dùng (0..1, sàn an toàn 0.45
 * cho chế độ banking) — UI không hiện số này cho người dùng, chỉ hiện thanh trượt "Độ rõ ảnh"
 * (xem `components/PhotoHalftoneStep.tsx`).
 *
 * `detailLevel` — "Độ chi tiết ảnh" (mặc định 'fine' ở server nếu không truyền) — ép QR dùng
 * nhiều module hơn để ảnh nền hiện rõ hơn (độ phân giải ảnh trong QR halftone bị giới hạn
 * CHÍNH XÁC bằng số module, xem apps/api/src/render/halftone-detail-level.ts VÀ
 * apps/api/src/render/photo-detail-level.ts — bảng số KHÁC NHAU tuỳ `style`). UI hiện 3 lựa
 * chọn bằng tiếng Việt đời thường ("Vừa phải"/"Rõ"/"Rất rõ"), không hiện tên kỹ thuật
 * normal/fine/ultra cho người dùng (cùng nguyên tắc "không hiện số/thuật ngữ kỹ thuật" ở trên).
 *
 * `style` — hai kỹ thuật trộn ảnh ĐỘC LẬP (xem apps/api/src/schemas.ts halftoneStyleSchema):
 *  - `'photo'` (MẶC ĐỊNH) — "Giữ nguyên ảnh": giữ ảnh gốc 100%, chỉ vẽ chấm nhỏ mang dữ liệu.
 *    Nhìn rõ ảnh nhất, nhưng ở vùng ranh giới với một số máy quét cũ (luôn nhắc quét thử).
 *  - `'blend'` — "Hoà vào mã": thuật toán cũ, mã chắc chắn quét được hơn nhưng ảnh mờ hơn hẳn.
 * Server có thể tự đổi `'photo'` -> `'blend'` nếu ảnh quá khó (xem `styleUsed` != `style` +
 * cảnh báo trong `warnings`) — KHÔNG BAO GIỜ đổi âm thầm.
 */
export type HalftoneDetailLevel = "normal" | "fine" | "ultra";
export type HalftoneStyle = "blend" | "photo";

export type HalftoneRequestBody = {
  qrData: VietQRData;
  blendStrength?: number;
  modulePx?: number;
  detailLevel?: HalftoneDetailLevel;
  /** 'blend' | 'photo' — mặc định 'photo' nếu không truyền. */
  style?: HalftoneStyle;
  allowLowBlend?: boolean;
};

export type HalftoneResponse = {
  payload: string;
  imageUrl: string;
  width: number;
  height: number;
  blendStrength: number;
  modulePx: number;
  /** true khi server đã tự chỉnh (tăng blendStrength, hạ detailLevel, và/hoặc đổi style) so với yêu cầu gốc để đảm bảo quét được. */
  autoAdjusted: boolean;
  /** Kiểu trộn ảnh CLIENT đã yêu cầu (echo lại nguyên vẹn — dùng để so sánh với `styleUsed`). */
  style: HalftoneStyle;
  /** Kiểu trộn ảnh THỰC SỰ dùng cho ảnh trả về — khác `style` khi server đã tự đổi 'photo' -> 'blend' để đảm bảo quét được. */
  styleUsed: HalftoneStyle;
  /** Tỉ lệ đường kính chấm (style 'photo') hoặc chấm giữa (style 'blend') / kích thước module đã dùng cho ảnh trả về. */
  dotRatio: number;
  /** Mức chi tiết ảnh CLIENT đã yêu cầu (echo lại nguyên vẹn — dùng để so sánh với `detailLevelUsed`). */
  detailLevel: HalftoneDetailLevel;
  /** Mức chi tiết ảnh THỰC SỰ dùng cho ảnh trả về — khác `detailLevel` khi server đã tự hạ mức để đảm bảo quét được. */
  detailLevelUsed: HalftoneDetailLevel;
  /** Số module dữ liệu của QR đã dùng — "độ phân giải ảnh" hiệu dụng. */
  moduleCount: number;
  /** Kích thước cạnh QR tối thiểu khuyến nghị khi in/hiển thị (mm) — ƯỚC LƯỢNG, xem `recommendedMinPrintSizeNote`. */
  recommendedMinPrintSizeMm: number;
  /** Ghi chú tiếng Việt: đây là ước lượng, chưa kiểm bằng điện thoại thật. */
  recommendedMinPrintSizeNote: string;
  /** Kết quả decode-back thật bằng ZXing (decoder chính) trên đúng ảnh trả về. */
  zxingOk: boolean;
  /** Kết quả decode-back thật bằng jsQR (tín hiệu phụ, khắt khe hơn máy quét thật) trên đúng ảnh trả về. */
  jsqrOk: boolean;
  scanability: ScanabilityResult;
  warnings: string[];
};

/** POST /api/v1/uploads/:fileId/halftone */
export function createHalftoneQr(fileId: string, body: HalftoneRequestBody): Promise<HalftoneResponse> {
  return request<HalftoneResponse>(`/uploads/${fileId}/halftone`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}
